import * as Sentry from '@sentry/react';
import { toast } from 'sonner';

type GetNormalizedAmountArgs = {
  /** The numeric amount to normalize. Must be a finite number within safe integer bounds */
  amount: number;
  /** If true, returns positive amount. If false, returns negative amount */
  isCredit: boolean;
};

/**
 * Normalizes a numeric amount based on whether it's a credit or debit
 *
 * This function gracefully handles invalid inputs by returning sensible fallbacks
 * while logging issues to Sentry for monitoring.
 *
 * @param {GetNormalizedAmountArgs} args - The input parameters
 * @returns {number} The normalized amount (positive for credits, negative for debits)
 *
 * Fallback behavior:
 * - undefined/null amount: returns 0
 * - undefined/null isCredit: defaults to false (debit)
 * - invalid numbers (NaN, Infinity): returns 0
 * - numbers exceeding safe integer bounds: returns capped value
 *
 * @example
 * getNormalizedAmount({ amount: 100, isCredit: true }) // returns 100
 * getNormalizedAmount({ amount: 100, isCredit: false }) // returns -100
 * getNormalizedAmount({ amount: undefined, isCredit: true }) // returns 0 (with logging)
 * getNormalizedAmount({ amount: 100, isCredit: undefined }) // returns -100 (defaults to debit)
 */
export const getNormalizedAmount = ({ amount, isCredit }: GetNormalizedAmountArgs): number => {
  if (amount === undefined || amount === null) {
    Sentry.addBreadcrumb({
      category: 'get_normalized_amount',
      message: 'amount parameter is undefined or null - using fallback value 0',
      level: 'warning',
      data: {
        amount,
        isCredit,
        amountType: typeof amount,
        isCreditType: typeof isCredit,
        fallbackUsed: 0,
      },
    });

    Sentry.captureException(
      new Error(`Amount parameter is ${amount === undefined ? 'undefined' : 'null'}, using fallback value 0`),
    );

    return 0;
  }

  if (isCredit === undefined || isCredit === null) {
    Sentry.addBreadcrumb({
      category: 'get_normalized_amount',
      message: 'isCredit parameter is undefined or null - defaulting to false (debit)',
      level: 'warning',
      data: {
        amount,
        isCredit,
        amountType: typeof amount,
        isCreditType: typeof isCredit,
        fallbackUsed: false,
      },
    });

    Sentry.captureException(
      new Error(`isCredit parameter is ${isCredit === undefined ? 'undefined' : 'null'}, defaulting to false`),
    );

    // Default to debit behavior
    isCredit = false;
  }

  // Handle invalid numbers (NaN, Infinity, etc.)
  if (!Number.isFinite(amount)) {
    Sentry.addBreadcrumb({
      category: 'get_normalized_amount',
      message: 'Amount is not a finite number - using fallback value 0',
      level: 'warning',
      data: {
        amount,
        isCredit,
        amountType: typeof amount,
        isCreditType: typeof isCredit,
        fallbackUsed: 0,
      },
    });

    Sentry.captureException(
      new Error(`Amount must be a finite number, received: ${amount} (type: ${typeof amount}), using fallback value 0`),
    );

    return 0;
  }

  if (Math.abs(amount) > Number.MAX_SAFE_INTEGER) {
    const cappedAmount = amount > 0 ? Number.MAX_SAFE_INTEGER : -Number.MAX_SAFE_INTEGER;

    Sentry.addBreadcrumb({
      category: 'get_normalized_amount',
      message: 'Amount exceeds maximum safe integer - capping to safe value',
      level: 'warning',
      data: {
        originalAmount: amount,
        cappedAmount,
        isCredit,
        amountType: typeof amount,
        isCreditType: typeof isCredit,
      },
    });

    Sentry.captureException(new Error(`Amount exceeds maximum safe integer: ${amount}, capped to: ${cappedAmount}`));

    toast.warning('Transaction amount was unusually large and has been adjusted for display');

    amount = cappedAmount;
  }

  if (amount === 0 || Object.is(amount, -0)) {
    return 0;
  }

  const absoluteAmount = Math.abs(amount);

  return isCredit ? absoluteAmount : -absoluteAmount;
};
